// A Page (a Result of kind html) in the Result canvas: what did not carry over.
// Namespace `page-frame`. Placeholders are `{name}`, filled with `formatCopy`.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  pageNotesTitle: 'Parts of this page did not carry over',
  pageNotesBody:
    'Poneva could not copy everything this page loads from other sites ({count}), so some parts may look or work differently.',
};
export type PageFrameCopyKey = keyof typeof en;

export const pageFrameCopy: NamespaceCatalog<PageFrameCopyKey> = {
  en,
  sv: {
    pageNotesTitle: 'Delar av sidan följde inte med',
    pageNotesBody:
      'Poneva kunde inte kopiera allt som sidan hämtar från andra webbplatser ({count}), så vissa delar kan se ut eller fungera annorlunda.',
  },
  da: {
    pageNotesTitle: 'Dele af siden kom ikke med',
    pageNotesBody:
      'Poneva kunne ikke kopiere alt, som siden henter fra andre websteder ({count}), så nogle dele kan se anderledes ud eller fungere anderledes.',
  },
  de: {
    pageNotesTitle: 'Teile dieser Seite wurden nicht übernommen',
    pageNotesBody:
      'Poneva konnte nicht alles kopieren, was die Seite von anderen Websites lädt ({count}). Einige Teile sehen daher möglicherweise anders aus oder funktionieren anders.',
  },
  fr: {
    pageNotesTitle: 'Des éléments de cette page n’ont pas été repris',
    pageNotesBody:
      'Poneva n’a pas pu copier tout ce que la page charge depuis d’autres sites ({count}) ; certains éléments peuvent donc s’afficher ou fonctionner différemment.',
  },
  es: {
    pageNotesTitle: 'Algunas partes de esta página no se trasladaron',
    pageNotesBody:
      'Poneva no pudo copiar todo lo que la página carga de otros sitios ({count}), así que algunas partes pueden verse o funcionar de otra manera.',
  },
  it: {
    pageNotesTitle: 'Alcune parti di questa pagina non sono state importate',
    pageNotesBody:
      'Poneva non ha potuto copiare tutto ciò che la pagina carica da altri siti ({count}), quindi alcune parti potrebbero apparire o funzionare in modo diverso.',
  },
  pt: {
    pageNotesTitle: 'Partes desta página não foram trazidas',
    pageNotesBody:
      'A Poneva não conseguiu copiar tudo o que a página carrega de outros sites ({count}), por isso algumas partes podem ter outro aspeto ou funcionar de forma diferente.',
  },
  nl: {
    pageNotesTitle: 'Delen van deze pagina zijn niet meegekomen',
    pageNotesBody:
      'Poneva kon niet alles kopiëren wat de pagina van andere sites laadt ({count}), dus sommige onderdelen zien er mogelijk anders uit of werken anders.',
  },
  fi: {
    pageNotesTitle: 'Osa sivusta ei siirtynyt mukaan',
    pageNotesBody:
      'Poneva ei voinut kopioida kaikkea, mitä sivu lataa muilta sivustoilta ({count}), joten jotkin osat voivat näyttää tai toimia eri tavalla.',
  },
  et: {
    pageNotesTitle: 'Osa sellest lehest ei jõudnud kaasa',
    pageNotesBody:
      'Poneva ei saanud kopeerida kõike, mida leht teistelt saitidelt laadib ({count}), seega võivad mõned osad välja näha või töötada teisiti.',
  },
  el: {
    pageNotesTitle: 'Μέρη αυτής της σελίδας δεν μεταφέρθηκαν',
    pageNotesBody:
      'Το Poneva δεν μπόρεσε να αντιγράψει ό,τι φορτώνει η σελίδα από άλλους ιστότοπους ({count}), οπότε ορισμένα μέρη μπορεί να φαίνονται ή να λειτουργούν διαφορετικά.',
  },
  ro: {
    pageNotesTitle: 'Unele părți ale acestei pagini nu au fost preluate',
    pageNotesBody:
      'Poneva nu a putut copia tot ce încarcă pagina de pe alte site-uri ({count}), așa că unele părți pot arăta sau funcționa diferit.',
  },
  lt: {
    pageNotesTitle: 'Dalis šio puslapio neperkelta',
    pageNotesBody:
      '„Poneva“ negalėjo nukopijuoti visko, ką puslapis įkelia iš kitų svetainių ({count}), todėl kai kurios dalys gali atrodyti ar veikti kitaip.',
  },
  lv: {
    pageNotesTitle: 'Daļa šīs lapas netika pārnesta',
    pageNotesBody:
      'Poneva nevarēja nokopēt visu, ko lapa ielādē no citām vietnēm ({count}), tāpēc dažas daļas var izskatīties vai darboties citādi.',
  },
  ga: {
    pageNotesTitle: 'Níor tháinig codanna den leathanach seo leis',
    pageNotesBody:
      'Níorbh fhéidir le Poneva gach rud a luchtaíonn an leathanach ó shuíomhanna eile a chóipeáil ({count}), mar sin d’fhéadfadh cuid de na codanna breathnú nó oibriú ar bhealach difriúil.',
  },
  mt: {
    pageNotesTitle: 'Partijiet minn din il-paġna ma ġewx magħha',
    pageNotesBody:
      'Poneva ma setgħetx tikkopja kollox dak li l-paġna tgħabbi minn siti oħra ({count}), u għalhekk xi partijiet jistgħu jidhru jew jaħdmu b’mod differenti.',
  },
  bg: {
    pageNotesTitle: 'Части от тази страница не бяха пренесени',
    pageNotesBody:
      'Poneva не успя да копира всичко, което страницата зарежда от други сайтове ({count}), затова някои части може да изглеждат или да работят по различен начин.',
  },
  cs: {
    pageNotesTitle: 'Části této stránky se nepřenesly',
    pageNotesBody:
      'Poneva nemohla zkopírovat vše, co stránka načítá z jiných webů ({count}), takže některé části mohou vypadat nebo fungovat jinak.',
  },
  hr: {
    pageNotesTitle: 'Dijelovi ove stranice nisu preneseni',
    pageNotesBody:
      'Poneva nije mogla kopirati sve što stranica učitava s drugih web-mjesta ({count}), pa neki dijelovi mogu izgledati ili raditi drukčije.',
  },
  hu: {
    pageNotesTitle: 'Az oldal egyes részei nem kerültek át',
    pageNotesBody:
      'A Poneva nem tudott mindent lemásolni, amit az oldal más webhelyekről tölt be ({count}), ezért egyes részek másképp nézhetnek ki vagy működhetnek.',
  },
  pl: {
    pageNotesTitle: 'Części tej strony nie zostały przeniesione',
    pageNotesBody:
      'Poneva nie mogła skopiować wszystkiego, co strona ładuje z innych witryn ({count}), więc niektóre elementy mogą wyglądać lub działać inaczej.',
  },
  sk: {
    pageNotesTitle: 'Niektoré časti tejto stránky sa neprenesli',
    pageNotesBody:
      'Poneva nemohla skopírovať všetko, čo stránka načítava z iných webov ({count}), takže niektoré časti môžu vyzerať alebo fungovať inak.',
  },
  sl: {
    pageNotesTitle: 'Nekateri deli te strani niso bili preneseni',
    pageNotesBody:
      'Poneva ni mogla kopirati vsega, kar stran nalaga z drugih spletnih mest ({count}), zato so nekateri deli lahko videti drugače ali drugače delujejo.',
  },
};
export default pageFrameCopy;
